import React, { createContext, useEffect } from 'react'
import { FormProvider } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { Box, Grid, Step, Stepper, Typography } from '@mui/material'

import { PageContentLayout } from '@layouts/PageContentLayout'
import { AppLoader } from '@components/AppLoader'
import { EditConfigMenu } from '@components/ConfigEditMenu'
import { EditConfigScriptDialog } from '@components/EditConfigScriptDialog'
import { GeneralButtons } from '@components/GeneralButtons'

import {
  resetLeftEvents,
  resetTitle,
  setLeftEvents,
  setTitle,
} from '@redux/reducers/appFooter.reducer'

import { EditConfigProvider } from '@providers/EditConfigProvider'

import { useAppDispatch, useAuth, useSubscribeEvent } from '@hooks'
import { ENTITY_CONFIG_TYPE, TIME_LINE_CREATE_OR_EDIT_STEPS, USER_ROLES } from '@constants'
import { TimeLineCreateOrEditPageContextType } from '@types'

import { StepLabelWrap } from '@gantt/components/GanttCreateOrEdit/components/GanttConfiguration/components'

import { EVENT_NAME } from '../../events'

import { GeneralConfiguration } from './components/GeneralConfiguration'
import { MainObjectConfiguration } from './components/MainObjectConfiguration'
import { ObjectXConfiguration } from './components/ObjectXConfiguration'
import { ObjectYConfiguration } from './components/ObjectYConfiguration'
import { getIsStepFailed, getStepErrorFields } from './helpers'
import { useTimeLineCreateOrEdit } from './hooks'

export const PageContext = createContext({} as TimeLineCreateOrEditPageContextType)

export const TimeLineCreateOrEdit = () => {
  const dispatch = useAppDispatch()

  const [searchParams] = useSearchParams()
  const { t } = useTranslation()
  const { state, handlers, inputs, data } = useTimeLineCreateOrEdit()
  const { hasRole } = useAuth()
  const navigate = useNavigate()

  const {
    methods,
    globalDirty,
    isTimeLineLoading,
    activeStep,
    isCreateOrUpdateLoading,
    watchCode,
    watchTitle,
    watchId,
  } = state

  const { handleSaveTimeLine, handleCancelTimeLine, handleChangeActiveStep } = handlers

  const isAdmin = hasRole([USER_ROLES.ADMIN])
  const cameFromTimeline = searchParams.get('cameFrom')

  const {
    handleSubmit,
    formState: { errors },
  } = methods

  const contextValue = {
    data,
    handlers,
    state,
    inputs,
  }

  const renderContent = () => {
    switch (activeStep) {
      case 0:
        return <GeneralConfiguration />
      case 1:
        return <ObjectYConfiguration />
      case 2:
        return <ObjectXConfiguration />
      case 3:
        return <MainObjectConfiguration />
    }
  }

  const returnToTimeLine = () => {
    navigate(`${cameFromTimeline}`)
  }

  useSubscribeEvent(EVENT_NAME.RETURN_TO_TIMELINE, returnToTimeLine, !cameFromTimeline)

  useEffect(() => {
    if (watchId) {
      dispatch(
        setTitle(
          t('timeLinesCreateOrEdit.footerConfigInfo', { code: watchCode, title: watchTitle })
        )
      )
    }

    if (cameFromTimeline) {
      dispatch(
        setLeftEvents([
          {
            label: t('timeLinesCreateOrEdit.returnToTimeline'),
            icon: 'format_list_bulleted',
            type: EVENT_NAME.RETURN_TO_TIMELINE,
          },
        ])
      )
    }

    return () => {
      dispatch(resetLeftEvents())
      dispatch(resetTitle())
    }
  }, [cameFromTimeline, isAdmin, dispatch, t, watchId, watchCode, watchTitle])

  return (
    <PageContext.Provider value={contextValue}>
      <EditConfigProvider entityType={ENTITY_CONFIG_TYPE.TIMELINES}>
        <PageContentLayout sx={{ p: 0 }}>
          <EditConfigScriptDialog />
          <Box sx={{ height: '100%', overflow: 'auto', scrollbarGutter: 'stable', p: 1 }}>
            <Grid container alignItems={'center'} justifyContent={'space-between'} py={0.5}>
              <Typography mb={1} variant='h5'>
                {t(`timeLinesCreateOrEdit.header`)}
              </Typography>
              {watchId && <EditConfigMenu configId={watchId} />}
            </Grid>
            {isTimeLineLoading ? (
              <AppLoader />
            ) : (
              <FormProvider {...methods}>
                <Stepper alternativeLabel activeStep={activeStep}>
                  {TIME_LINE_CREATE_OR_EDIT_STEPS.map((label, index) => (
                    <Step key={label} disabled={false}>
                      <StepLabelWrap
                        key={label}
                        errors={errors}
                        getIsStepFailed={getIsStepFailed}
                        getStepErrorFields={getStepErrorFields}
                        label={label}
                        onClick={() => handleChangeActiveStep(index)}
                      />
                    </Step>
                  ))}
                </Stepper>

                {renderContent()}
              </FormProvider>
            )}
          </Box>
        </PageContentLayout>
        <Box
          sx={{
            position: 'sticky',
            bottom: 0,
            width: '100%',
            flexShrink: 0,
          }}
        >
          <Grid
            container
            direction='column'
            sx={{
              background: 'white',
              borderTop: '1px solid',
              borderColor: 'grey.300',
              boxShadow: '0 -1px 3px rgba(0,0,0,0.05)',
            }}
          >
            <Grid item>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'flex-end',
                  pt: 1,
                  pb: 1,
                  pr: 1,
                }}
              >
                <GeneralButtons
                  cancelText={t('timeLinesCreateOrEdit.cancelButton')}
                  isLoading={isTimeLineLoading || isCreateOrUpdateLoading}
                  saveDisabled={!globalDirty || isTimeLineLoading || isCreateOrUpdateLoading}
                  saveText={t('timeLinesCreateOrEdit.saveButton')}
                  onCancel={handleCancelTimeLine}
                  onSave={handleSubmit(handleSaveTimeLine)}
                />
              </Box>
            </Grid>
          </Grid>
        </Box>
      </EditConfigProvider>
    </PageContext.Provider>
  )
}
